import React from 'react';
import { defineMessages, FormattedMessage } from 'react-intl';
import { StyleSheet, View } from 'react-native';
import type { AddressType } from '@sg/garnish';
import { BodyText, Ledger as LedgerComponent } from '@sg/garnish';
import type { CourierDetails } from '@sg/graphql-schema';
import type { Ledger } from '@sg/graphql-schema';
import {
  type DeliveryPreferenceType,
  type FlattenedOrderStatuses,
} from '@sg/graphql-schema';

import { type PartialLineItem } from '../../../../graphql';
import { type ReorderOrder } from '../../../../hooks';
import { useLedgerLineItems } from '../../hooks';
import { OrderStatusLineItems } from '../OrderStatusLineItems';

export const OrderStatusLedger = (props: OrderStatusLedgerProps) => {
  const { order, lineItems, ledger } = props;

  const ledgerTip = ledger.tip ?? 0;
  const ledgerItems = useLedgerLineItems({
    ledger,
    showTip: ledgerTip > 0,
    showTotal: true,
    showTaxes: true,
    showReward: true,
  });

  return (
    <View>
      <View>
        <BodyText sizeMatch={['16']} bold>
          <FormattedMessage {...messages.yourOrder} />
        </BodyText>

        <OrderStatusLineItems order={order} lineItems={lineItems} />
      </View>

      <LedgerComponent
        style={styles.ledger}
        hasTopBorder={true}
        items={ledgerItems}
      />
    </View>
  );
};

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  yourOrder: {
    defaultMessage: 'Your order',
    description: 'Order Status | Ledger | Your order',
  },
});

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  ledger: {
    paddingBottom: 0,
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type OrderStatusLedgerProps = Readonly<{
  order: ReorderOrder;
  orderType: AddressType;
  orderStatus?: FlattenedOrderStatuses;
  deliveryAddressName?: string;
  deliveryAddressStreet?: string;
  deliveryAddressSecondaryStreet?: string;
  deliveryAddressCity?: string;
  deliveryAddressState?: string;
  deliveryAddressZipCode?: string;
  deliveryAddressNotes?: string;
  deliveryPreference?: DeliveryPreferenceType;
  locationName?: string;
  locationAddress?: string;
  locationCity?: string;
  locationState?: string;
  locationZipCode?: string;
  lineItems: readonly PartialLineItem[];
  dropoffLocation?: string;
  ledger: Partial<Ledger>;
  courierName?: CourierDetails['name'];
  courierPhone?: CourierDetails['phone'];
}>;
